import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Collapse, Divider, IconButton, Paper, Stack, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import MilestonePanel from './MilestonePanel';
import AdmitTruck from '../operations/AdmitTruck';
import PostedGate from '../operations/PostedGate';
import { useOperations } from '../operations/OperationsProvider';
import { driverLabel } from '../operations/drivers';
import { DocumentWarning } from '../operations/documents';
import ScanChallan from '../operations/ScanChallan';
import { accentChipSx, stageAccent, tintedPanelSx } from '../common/theme/accents';

const normalize = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const phaseLabel = (value) => value?.replaceAll('_', ' ').toLowerCase().replace(/^./, (s) => s.toUpperCase()) || 'Unknown';

// The desk starts from what gate and weighbridge staff can see: the number on the truck or the
// challan in the driver's hand. Three ways in, because gates differ: typed, read off the challan by
// camera, or sent by a barcode or RFID gun, which types into the focused field like a keyboard and
// needs nothing here at all. The camera is the one that had no button - the desk said "scan" and
// offered nothing to press unless the yard had bought hardware.
// A truck that was never registered at the parking is admitted here instead of being turned away:
// the same admission the parking counter does, plus the entry stage, in one step.
const GateDesk = ({ trips, basePath }) => {
  const navigate = useNavigate();
  const { data } = useOperations();
  const [query, setQuery] = useState('');
  const [admitting, setAdmitting] = useState(true);
  const [scanning, setScanning] = useState(false);
  // One read of the challan answers both questions this desk asks. It fills the lookup, and if no
  // open trip answers to it the same read - number and photograph - seeds the admission below.
  const [scanned, setScanned] = useState(null);
  const key = normalize(query);
  // What the desk can read, said once and always visible, because "Scan" with no statement of what
  // it accepts is the complaint that started this. A hardware barcode or RFID gun needs nothing
  // here: it types into the focused field like a keyboard.
  // Asked of the control that actually does the capture. Scanning goes through a file input with
  // capture="environment", never through getUserMedia, and a web view hands that straight to the
  // system camera while exposing no navigator.mediaDevices at all - so probing for a live stream
  // hid the scanner on exactly the devices it was built for.
  const camera = typeof document !== 'undefined' && 'capture' in document.createElement('input');
  const answering = key.length < 3 ? [] : trips
    .filter((t) => !t.closedAt && [`Q-${t.id}`, t.vehicle, t.challanNumber, t.shipmentId, t.consignmentId].some((value) => normalize(value).includes(key)));
  // Only a few are shown, but how many there are is counted before that — telling somebody three
  // trips match when seven do sends them looking for a fourth that is there and hidden.
  const matches = answering.slice(0, 3);
  return (
    <Paper variant="outlined" sx={[{ p: 2.5, mb: 3 }, tintedPanelSx('teal')]}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }} justifyContent="space-between">
        <Typography variant="h6">Plant entry</Typography>
        <PostedGate />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            fullWidth
            autoFocus
            label="Vehicle, challan or trip number"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              inputProps: { maxLength: 60 },
              endAdornment: query ? (
                <IconButton size="small" aria-label="Clear" onClick={() => setQuery('')}><CloseIcon fontSize="small" /></IconButton>
              ) : null,
            }}
          />
        </Box>
        <Button variant="contained" size="large" startIcon={<PhotoCameraIcon />} sx={{ whiteSpace: 'nowrap', height: 56 }} onClick={() => setScanning(true)}>Scan</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
        {`${camera ? 'Camera ready — scan a printed code or photograph the challan.' : 'No camera on this browser — type the number, or use a photograph.'} A barcode or RFID scanner types straight into this field.`}
      </Typography>
      <ScanChallan
        open={scanning}
        onClose={() => setScanning(false)}
        onScanned={({ challanNumber, imageDataUrl, fields }) => {
          if (challanNumber) setQuery(challanNumber);
          // Kept whole rather than discarded: if this challan turns out to belong to no open trip,
          // the truck is admitted here, and everything the paper said fills that admission.
          setScanned({ challanNumber, imageDataUrl, fields, at: Date.now() });
        }}
      />
      {key.length >= 3 && !matches.length && !admitting && (
        <Alert
          severity="info"
          sx={{ mt: 2 }}
        >
          No open trip matches a vehicle number, challan or trip number containing that. Check the number — or, if the truck arrived straight at the gate without being registered at the parking, admit it below.
        </Alert>
      )}
      {matches.length > 1 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
          {answering.length > matches.length
            ? `${answering.length} open trips answer to that; the first ${matches.length} are below. Add a character or two to narrow it.`
            : `${answering.length} open trips answer to that. Add a character or two to narrow it.`}
        </Typography>
      )}
      {!!matches.length && (
        <Stack gap={2} sx={{ mt: 2 }}>
          {matches.map((trip) => (
            <Paper key={trip.id} variant={matches.length === 1 ? 'elevation' : 'outlined'} elevation={matches.length === 1 ? 3 : 0} sx={{ p: matches.length === 1 ? 2.5 : 2 }}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
                <Typography fontWeight={700} variant={matches.length === 1 ? 'h6' : 'body1'}>{trip.vehicle}</Typography>
                <Chip size="small" label={phaseLabel(trip.phase)} sx={accentChipSx(stageAccent(trip.phase))} />
                <Button size="small" onClick={() => navigate(`${basePath}/${trip.id}`)} sx={{ ml: 'auto' }}>Open trip</Button>
              </Stack>
              <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>
                {`Q-${trip.id} · Challan ${trip.challanNumber || trip.shipmentId} · ${trip.transporter || 'No transporter recorded'}${driverLabel(data.drivers, trip) ? ` · ${driverLabel(data.drivers, trip)}` : ''}`}
              </Typography>
              {/* Verifying at the gate is the moment a lapsed permit has to be visible. */}
              <DocumentWarning vehicle={data.vehicles.find((v) => v.id === trip.vehicleId)} sx={{ mb: 1.5 }} />
              <MilestonePanel trip={trip} />
            </Paper>
          ))}
        </Stack>
      )}
      {!matches.length && (
        <Stack direction="row" alignItems="center" sx={{ mt: 2.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>Admit a truck at the gate</Typography>
          <Button size="small" sx={{ ml: 'auto' }} onClick={() => setAdmitting((open) => !open)}>
            {admitting ? 'Hide' : 'Show'}
          </Button>
        </Stack>
      )}
      <Collapse in={admitting && !matches.length} unmountOnExit>
        <Divider sx={{ mb: 2.5 }} />
        <AdmitTruck
          desk="GATE"
          scan={false}
          scanned={scanned}
          onAdmitted={(trip) => { setScanned(null); setQuery(trip.vehicle || ''); }}
        />
      </Collapse>
    </Paper>
  );
};

export default GateDesk;
